
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";

interface Persona {
  id: string;
  brief_id: string;
  user_id: string;
  brand_id: string | null;
  name: string;
  created_at: string;
  updated_at: string;
  avatar_url: string | null;
  // New detailed columns
  persona_name: string | null;
  age: number | null;
  gender: string | null;
  job_title: string | null;
  company_type: string | null;
  bio: string | null;
  goals_motivations: string | null;
  frustrations: string | null;
  preferred_tools: string | null;
  sample_quote: string | null;
  basic_demographics: string | null;
  professional_background: string | null;
  user_needs_goals: string | null;
  information_sources: string | null;
  influencing_factors: string | null;
  site_access_context: string | null;
  regular_devices: string | null;
  software_applications: string | null;
  primary_web_device: string | null;
  daily_browsing_time: string | null;
  work_experience: string | null;
  communication_channels: string | null;
  technology_comfort: string | null;
  behavioral_patterns: string | null;
}

interface PersonaBrief {
  id: string;
  name: string;
  form_data: any;
  generated_brief: string | null;
  created_at: string;
  updated_at: string;
  brand_id: string | null;
  user_id: string;
}

interface PersonaWithBrief extends Persona {
  brief: PersonaBrief;
}

export const usePersonas = () => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const [personas, setPersonas] = useState<PersonaWithBrief[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPersonas = async () => {
    if (!user || !currentBrandId) {
      setPersonas([]);
      setIsLoading(false);
      return;
    }

    try {
      // Filter personas by current brand ID
      const { data, error } = await supabase
        .from('personas')
        .select(`
          *,
          brief:personas_briefs(*)
        `)
        .eq('brand_id', currentBrandId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match our interface
      const transformedData = (data || []).map(persona => ({
        ...persona,
        brief: Array.isArray(persona.brief) ? persona.brief[0] : persona.brief
      })) as PersonaWithBrief[];

      setPersonas(transformedData);
    } catch (error: any) {
      console.error('Error fetching personas:', error);
      setPersonas([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonas();
  }, [user, currentBrandId]);

  // Listen for personas generation events
  useEffect(() => {
    const handlePersonasGenerated = () => {
      fetchPersonas();
    };

    window.addEventListener('personasGenerated', handlePersonasGenerated);
    
    return () => {
      window.removeEventListener('personasGenerated', handlePersonasGenerated);
    };
  }, []);

  return { personas, isLoading, refetch: fetchPersonas };
};
